// Ellie's chat in Eleplan for Chrome's side panel (app.eleplan.com/panel.html, framed by
// the extension's own panel page). "Ask Ellie about it" on a page hands this chat what was
// pointed at, by postMessage: this page says it's ready, to the extension that framed it
// and no one else, and takes an ask only from that frame's parent at that origin. What
// arrives is a website's text: it waits here as a chip until the person sends a message,
// and the server keeps it apart from their words (page_context, never the prompt).
import { useEffect, useState } from 'react';
import { CHROME_EXTENSION_IDS } from './chromeExtension';
import { isChromePanel } from './platform';

export const PANEL_READY = 'eleplan/panel-ready';
export const PANEL_ASK = 'eleplan/ask';
// The conversation to show: a browser check just approved in a Chrome tab of the app.
export const PANEL_OPEN_CONVERSATION = 'eleplan/open-conversation';
// This page is going away (a reload, a sign-in): the panel waits for its next ready.
export const PANEL_GONE = 'eleplan/panel-gone';

// The same limits the extension and the server keep.
const MAX_TEXT_CHARS = 8000;
const MAX_NAME_CHARS = 120;
const MAX_TITLE_CHARS = 200;
const MAX_URL_CHARS = 2000;
const SITE = /^[a-z0-9.-]{1,253}$/i;

export interface PageAsk { text: string; name: string; site: string; url: string | null; title: string }
export interface PanelOpen { projectId: number; conversationId?: string; accountId?: string }

const CONVERSATION_ID = /^[A-Za-z0-9._~-]{1,256}$/;
const ACCOUNT_ID = /^[A-Za-z0-9._~-]{1,255}$/;

/** The ids the extension sent, if they're exactly ids; null otherwise. The chat loads them as the person. */
export function parsePanelOpen(raw: unknown): PanelOpen | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const { project_id: project, conversation_id: conversation, account_id: account } = raw as Record<string, unknown>;
  if (!Number.isSafeInteger(project) || (project as number) <= 0) return null;
  if (conversation != null && (typeof conversation !== 'string' || !CONVERSATION_ID.test(conversation))) return null;
  if (account != null && (typeof account !== 'string' || !ACCOUNT_ID.test(account))) return null;
  // With the account, the queue proves membership and the plan's pairing before it opens (desktop handoff's check).
  return {
    projectId: project as number,
    ...(typeof conversation === 'string' ? { conversationId: conversation } : {}),
    ...(typeof account === 'string' ? { accountId: account } : {}),
  };
}

const capped = (value: unknown, max: number): string => (typeof value === 'string' ? value.replace(/\s+/g, ' ').trim().slice(0, max) : '');

/** What the extension sent, if it's exactly the shape it sends; null otherwise. */
export function parsePageAsk(raw: unknown): PageAsk | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const ask = raw as Record<string, unknown>;
  const text = typeof ask.text === 'string' ? ask.text.trim().slice(0, MAX_TEXT_CHARS) : '';
  const site = typeof ask.site === 'string' ? ask.site.trim().toLowerCase() : '';
  if (!text || !SITE.test(site)) return null;
  let url: string | null = null;
  if (typeof ask.url === 'string' && ask.url.length <= MAX_URL_CHARS) {
    try { url = new URL(ask.url).protocol === 'https:' ? ask.url : null; } catch { url = null; }
  }
  return { text, site, url, name: capped(ask.name, MAX_NAME_CHARS), title: capped(ask.title, MAX_TITLE_CHARS) };
}

/** The extension that framed this page, by origin, only if it's one of ours. */
export function framingExtension(win: Window): string | null {
  if (win.parent === win) return null;
  const origin = win.location.ancestorOrigins?.[0] ?? null;
  return origin && CHROME_EXTENSION_IDS.some((id) => origin === `chrome-extension://${id}`) ? origin : null;
}

let current: PageAsk | null = null;
const listeners = new Set<(ask: PageAsk | null) => void>();
function setPageAsk(next: PageAsk | null): void {
  current = next;
  listeners.forEach((listener) => listener(next));
}

/** Sent, or taken off by the person. */
export function clearPageAsk(): void {
  setPageAsk(null);
}

// Who opens the conversation (RootNavigator, in the panel), and one that came before it listened.
let openListener: ((target: PanelOpen) => void) | null = null;
let pendingOpen: PanelOpen | null = null;

/** Opens each conversation the extension names. Returns the unsubscribe. */
export function subscribePanelOpen(listener: (target: PanelOpen) => void): () => void {
  openListener = listener;
  if (pendingOpen) { const target = pendingOpen; pendingOpen = null; listener(target); }
  return () => { if (openListener === listener) openListener = null; };
}

function openConversation(target: PanelOpen): void {
  if (openListener) openListener(target);
  else pendingOpen = target;
}

let listening = false;

/** Says this chat is ready, once, and from then on takes asks from the extension that framed it. */
export function startChromePanelBridge(win: Window): void {
  if (listening) return;
  const origin = framingExtension(win);
  if (!origin) return;
  listening = true;
  win.addEventListener('message', (event: MessageEvent) => {
    if (event.source !== win.parent || event.origin !== origin) return;
    const data = event.data as { type?: unknown; ask?: unknown } | null;
    if (data?.type === PANEL_OPEN_CONVERSATION) {
      const target = parsePanelOpen(data);
      if (target) openConversation(target);
      return;
    }
    if (data?.type !== PANEL_ASK) return;
    const ask = parsePageAsk(data.ask);
    if (ask) setPageAsk(ask);
  });
  win.addEventListener('pagehide', () => win.parent.postMessage({ type: PANEL_GONE }, origin));
  win.parent.postMessage({ type: PANEL_READY }, origin);
}

/** Hears every change to the waiting ask, starting with the current one. Returns the unsubscribe. */
export function subscribePageAsk(listener: (ask: PageAsk | null) => void): () => void {
  listeners.add(listener);
  listener(current);
  return () => { listeners.delete(listener); };
}

/** The ask waiting in the panel's composer, and how to take it off. Nothing outside the panel. */
export function usePageAsk(): [PageAsk | null, () => void] {
  const [ask, setAsk] = useState<PageAsk | null>(current);
  useEffect(() => {
    const unsubscribe = subscribePageAsk(setAsk);
    // Only the side panel's page listens to its frame's parent.
    if (isChromePanel && typeof window !== 'undefined') startChromePanelBridge(window);
    return unsubscribe;
  }, []);
  return [ask, clearPageAsk];
}

/** For tests: forget the listener and the ask. */
export function resetChromePanelAskForTests(): void {
  listening = false;
  current = null;
  listeners.clear();
  openListener = null;
  pendingOpen = null;
}
